import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { radarApi } from '../api';

// ─── SEVERITY ────────────────────────────────────────────────────────────────

const SEV_CONFIG = {
  critical: { label: 'Kritisch', color: '#ef4444', bg: '#450a0a', border: '#991b1b', icon: '🔴', aria: 'Kritisch' },
  high:     { label: 'Hoch',     color: '#f97316', bg: '#431407', border: '#9a3412', icon: '🟠', aria: 'Hoch' },
  medium:   { label: 'Mittel',   color: '#eab308', bg: '#422006', border: '#854d0e', icon: '🟡', aria: 'Mittel' },
  low:      { label: 'Niedrig',  color: '#22c55e', bg: '#052e16', border: '#15803d', icon: '🟢', aria: 'Niedrig' },
  info:     { label: 'Info',     color: '#64748b', bg: '#1e293b', border: '#334155', icon: '⚪', aria: 'Info'    },
};

const HORIZON_LABELS = {
  overdue:    '⚠️ Überfällig',
  today:      '🔥 Heute',
  next7days:  '📅 Nächste 7 Tage',
  next30days: '📆 Nächste 30 Tage',
  next90days: '🗓️ Nächste 90 Tage',
  later:      '🕐 Später',
  noDate:     '❓ Ohne Datum',
};

const HORIZON_ORDER = ['overdue','today','next7days','next30days','next90days','later','noDate'];

const CATEGORY_LABELS = {
  kuendigung:          '✂️ Kündigungsfrist',
  vertragsende:        '📅 Vertragsende',
  preis:               '💶 Preisrisiko',
  dokument:            '📄 Dokumentenrisiko',
  daten:               '📝 Datenpflege',
  erinnerung:          '🔔 Erinnerung',
  kuendigungsprozess:  '⚡ Kündigungsprozess',
};

const FACTTYPE_CONFIG = {
  FAKT:      { label: 'FAKT',       color: '#22c55e' },
  INDIKATOR: { label: 'INDIKATOR',  color: '#eab308' },
  SCHÄTZUNG: { label: 'SCHÄTZUNG', color: '#64748b' },
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function euro(v) {
  if (v == null || isNaN(v)) return '';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}

function daysLabel(days) {
  if (days === null || days === undefined) return '';
  if (days < 0) return `${Math.abs(days)} Tage überfällig`;
  if (days === 0) return 'Heute';
  if (days === 1) return 'Morgen';
  return `${days} Tage`;
}

// ─── RADAR CARD ───────────────────────────────────────────────────────────────

function RadarCard({ event, onOpenDetail }) {
  const sev = SEV_CONFIG[event.severity] || SEV_CONFIG.info;
  const factCfg = FACTTYPE_CONFIG[event.factType] || FACTTYPE_CONFIG['FAKT'];
  const catLabel = CATEGORY_LABELS[event.category] || event.category;

  return (
    <div
      role="article"
      aria-label={`${sev.aria}: ${event.title}`}
      style={{
        background: '#1e293b',
        border: `1px solid ${sev.border}`,
        borderLeft: `4px solid ${sev.color}`,
        borderRadius: 10,
        padding: '14px 16px',
        marginBottom: 10,
        cursor: 'pointer',
      }}
      onClick={() => onOpenDetail(event)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* Severity badge (not color-only: icon + aria-label) */}
        <div
          aria-label={sev.aria}
          title={sev.label}
          style={{
            width: 36, height: 36, borderRadius: 8,
            background: sev.bg, border: `1px solid ${sev.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, flexShrink: 0,
          }}
        >
          {sev.icon}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>{event.contractTitle}</span>
            <span style={{ fontSize: 11, color: '#64748b', background: '#0f172a', borderRadius: 4, padding: '1px 6px' }}>
              {catLabel}
            </span>
          </div>

          {/* Description */}
          <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 6 }}>{event.description}</div>

          {/* Footer row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {event.daysRemaining !== null && (
              <span style={{
                fontSize: 12, fontWeight: 600,
                color: event.daysRemaining < 0 ? '#ef4444' : event.daysRemaining === 0 ? '#f97316' : '#94a3b8',
              }}>
                ⏱ {daysLabel(event.daysRemaining)}
              </span>
            )}
            {event.dueDate && (
              <span style={{ fontSize: 11, color: '#475569' }}>
                {event.dueDate.slice(0, 10).split('-').reverse().join('.')}
              </span>
            )}
            <span
              title={`Datengrundlage: ${factCfg.label}`}
              style={{
                fontSize: 10, fontWeight: 700, borderRadius: 3, padding: '1px 5px',
                background: '#0f172a', color: factCfg.color, border: `1px solid ${factCfg.color}33`,
              }}
            >
              {factCfg.label}
            </span>
            {event.annualCost > 0 && (
              <span style={{ fontSize: 11, color: '#475569' }}>
                {euro(event.annualCost)}/Jahr
              </span>
            )}
          </div>
        </div>

        <div style={{ fontSize: 18, color: '#334155', flexShrink: 0 }}>›</div>
      </div>
    </div>
  );
}

// ─── DETAIL DRAWER ────────────────────────────────────────────────────────────

function DetailDrawer({ event, onClose }) {
  const navigate = useNavigate();
  if (!event) return null;
  const sev = SEV_CONFIG[event.severity] || SEV_CONFIG.info;
  const factCfg = FACTTYPE_CONFIG[event.factType] || FACTTYPE_CONFIG['FAKT'];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#1e293b', borderRadius: '16px 16px 0 0',
          width: '100%', maxHeight: '80vh', overflowY: 'auto',
          padding: '24px 20px',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 24 }}>{sev.icon}</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9' }}>{event.title}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{event.provider}</div>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost"
            style={{ marginLeft: 'auto' }}
            aria-label="Schließen"
          >✕</button>
        </div>

        <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 16 }}>{event.description}</div>

        {event.signals?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Signale</div>
            {event.signals.map((s, i) => (
              <div key={i} style={{ fontSize: 13, color: '#94a3b8', padding: '3px 0', borderBottom: '1px solid #0f172a' }}>
                {s}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
          {event.dueDate && (
            <div style={{ background: '#0f172a', borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Fällig am</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
                {event.dueDate.slice(0, 10).split('-').reverse().join('.')}
              </div>
            </div>
          )}
          {event.daysRemaining !== null && (
            <div style={{ background: '#0f172a', borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Verbleibend</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: event.daysRemaining < 0 ? '#ef4444' : '#f1f5f9' }}>
                {daysLabel(event.daysRemaining)}
              </div>
            </div>
          )}
          <div style={{ background: '#0f172a', borderRadius: 8, padding: '8px 12px' }}>
            <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Datengrundlage</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: factCfg.color }}>{factCfg.label}</div>
          </div>
          {event.annualCost > 0 && (
            <div style={{ background: '#0f172a', borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Jahreskosten</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>{euro(event.annualCost)}</div>
            </div>
          )}
        </div>

        {event.recommendedAction && (
          <button
            onClick={() => { onClose(); if (event.actionUrl) navigate(event.actionUrl); }}
            className="btn-primary"
            style={{ width: '100%' }}
          >
            {event.recommendedAction} →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── EXECUTIVE SUMMARY BAR ───────────────────────────────────────────────────

function SummaryBar({ summary }) {
  const s = summary || {};
  const items = [
    { label: 'Kritisch',       value: s.critical  || 0, color: '#ef4444', icon: '🔴' },
    { label: 'Hoch',           value: s.high      || 0, color: '#f97316', icon: '🟠' },
    { label: 'Überfällig',     value: s.overdue   || 0, color: '#ef4444', icon: '⚠️' },
    { label: '7 Tage',         value: s.next7days || 0, color: '#eab308', icon: '📅' },
  ];

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20,
    }}>
      {items.map(({ label, value, color, icon }) => (
        <div key={label} style={{
          background: '#1e293b', borderRadius: 10, padding: '12px 8px', textAlign: 'center',
          border: `1px solid ${value > 0 ? color + '55' : '#334155'}`,
        }}>
          <div style={{ fontSize: 22 }}>{icon}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: value > 0 ? color : '#64748b' }}>{value}</div>
          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── FILTER BAR ───────────────────────────────────────────────────────────────

function FilterBar({ filters, onChange, total, filtered }) {
  const selStyle = (active) => ({
    padding: '5px 10px', borderRadius: 6, border: '1px solid',
    cursor: 'pointer', fontSize: 12, fontWeight: active ? 700 : 400,
    background: active ? '#818cf8' : '#1e293b',
    borderColor: active ? '#818cf8' : '#334155',
    color: active ? '#fff' : '#94a3b8',
  });

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: '#64748b', alignSelf: 'center' }}>Schwere:</span>
        {[['', 'Alle'], ['critical', '🔴 Kritisch'], ['high', '🟠 Hoch'], ['medium', '🟡 Mittel'], ['low', '🟢 Niedrig']].map(([v, l]) => (
          <button key={v} onClick={() => onChange({ ...filters, severity: v })} style={selStyle(filters.severity === v)}>{l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: '#64748b', alignSelf: 'center' }}>Horizont:</span>
        {[['', 'Alle'], ['overdue', '⚠️ Überfällig'], ['today', '🔥 Heute'], ['next7days', '7 Tage'], ['next30days', '30 Tage']].map(([v, l]) => (
          <button key={v} onClick={() => onChange({ ...filters, horizon: v })} style={selStyle(filters.horizon === v)}>{l}</button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#64748b' }}>
        {filtered} von {total} Events angezeigt
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function RadarPage() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [filters, setFilters]   = useState({ severity: '', horizon: '' });
  const [detail, setDetail]     = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    radarApi.analyse()
      .then(d => { setData(d); setError(null); })
      .catch(e => setError(e?.response?.data?.error || 'Radar konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Client-seitig filtern (API gibt immer alle zurück; Filter sind zusätzlich)
  const events = data?.events || [];
  const filtered = events.filter(e => {
    if (filters.severity && e.severity !== filters.severity) return false;
    if (filters.horizon  && e.horizon  !== filters.horizon)  return false;
    return true;
  });

  // Nach Horizon gruppieren
  const grouped = {};
  HORIZON_ORDER.forEach(h => { grouped[h] = []; });
  filtered.forEach(e => { if (grouped[e.horizon]) grouped[e.horizon].push(e); });

  return (
    <div style={{ padding: '16px 16px 32px', maxWidth: 800, margin: '0 auto' }}>
      {/* Titel */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 28 }}>📡</span>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#f1f5f9' }}>Fristen- & Risikoradar</h1>
          <div style={{ fontSize: 12, color: '#64748b' }}>Was muss ich wann tun?</div>
        </div>
        <button
          onClick={load}
          className="btn-secondary"
          style={{ marginLeft: 'auto' }}
          aria-label="Radar neu laden"
        >
          ↻ Neu laden
        </button>
      </div>

      {/* Fehler */}
      {error && (
        <div style={{ background: '#450a0a', border: '1px solid #991b1b', color: '#fca5a5',
          borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Laden */}
      {loading && (
        <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>
          📡 Radar läuft...
        </div>
      )}

      {!loading && data && (
        <>
          {/* Executive Summary */}
          <SummaryBar summary={data.summary} />

          {/* Filter */}
          <FilterBar
            filters={filters}
            onChange={setFilters}
            total={events.length}
            filtered={filtered.length}
          />

          {/* Keine Events */}
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', color: '#64748b', padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#94a3b8' }}>Keine offenen Punkte</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                {events.length === 0 ? 'Der Radar findet keine Fristen, Risiken oder Handlungsbedarf.' : 'Alle Events sind durch den aktiven Filter ausgeblendet.'}
              </div>
            </div>
          )}

          {/* Timeline Sektionen */}
          {HORIZON_ORDER.filter(h => grouped[h]?.length > 0).map(h => (
            <div key={h} style={{ marginBottom: 24 }}>
              <div style={{
                fontSize: 13, fontWeight: 700, color: '#94a3b8',
                textTransform: 'uppercase', letterSpacing: 1,
                marginBottom: 10, paddingBottom: 4,
                borderBottom: '1px solid #334155',
              }}>
                {HORIZON_LABELS[h]} · {grouped[h].length}
              </div>
              {grouped[h].map(e => (
                <RadarCard key={e.id} event={e} onOpenDetail={setDetail} />
              ))}
            </div>
          ))}

          {/* Generiert-Zeitstempel */}
          {data.generatedAt && (
            <div style={{ fontSize: 11, color: '#334155', textAlign: 'center', marginTop: 8 }}>
              Generiert: {new Date(data.generatedAt).toLocaleString('de-DE')}
            </div>
          )}
        </>
      )}

      {/* Detail Drawer */}
      {detail && (
        <DetailDrawer event={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}
